<script setup lang="ts">
import { ref, computed } from 'vue';
import { UiCard, UiText, UiIcon, UiAside, UiProse, ClientReviewRequestSchema } from '@shopexperts/shared';
import { z } from 'zod';
import SubmitReviewForm from './SubmitReviewForm.vue';
import ExpertInfo from './shared/ExpertInfo.vue';
import type { ExpertData } from './ExpertDetailsCard.vue';

type ClientReviewRequest = z.infer<typeof ClientReviewRequestSchema>;

const props = defineProps<{
    request: ClientReviewRequest;
}>();

const emit = defineEmits<{
    reviewSubmitted: [];
}>();

const asideOpen = ref(false);
const formRef = ref<InstanceType<typeof SubmitReviewForm> | null>(null);

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};


const expertData = computed<ExpertData>(() => ({
    name: props.request.expert_name,
    type: props.request.expert_type,
    slug: props.request.expert_slug,
    profile_picture: props.request.expert_profile_picture,
}));

const isPending = computed(() => props.request.status === 'pending');

const openReviewAside = () => {
    formRef.value?.resetForm();
    asideOpen.value = true;
};

const handleReviewSuccess = () => {
    asideOpen.value = false;
    emit('reviewSubmitted');
};

const handleCancel = () => {
    asideOpen.value = false;
};
</script>

<template>
    <UiCard padding="md" class="bg-white">
        <div class="flex flex-col gap-6">
            <!-- Header: Expert info and date -->
            <div class="flex items-start justify-between gap-4">
                <ExpertInfo
                    :name="request.expert_name"
                    :profile-picture="request.expert_profile_picture"
                    :expert-type="request.expert_type"
                    role="Shopify Developer"
                />

                <div class="flex flex-col items-end gap-2 flex-shrink-0">
                    <UiText as="span" variant="p-small" class="text-gray-500 whitespace-nowrap"
                        :text="`Requested: ${formatDate(request.sent_at)}`" />
                </div>
            </div>

            <!-- Message box -->
            <div v-if="request.message" class="bg-se-gray-100 rounded-lg p-4">
                <UiText as="p" variant="caption-normal" class="mb-2 font-semibold" text="Experts message" />
                <UiProse :html="request.message" class="text-gray-900 text-sm" />
            </div>

            <!-- Write review link -->
            <div v-if="isPending" class="flex items-center gap-2">
                <UiIcon name="pencil" class="w-4 h-4 text-gray-600" />
                <button @click="openReviewAside"
                    class="text-gray-900 underline underline-offset-2 hover:text-se-primary transition-colors text-sm font-medium">
                    Write your review
                </button>
            </div>

            <!-- Already completed state -->
            <div v-else-if="request.status === 'completed'" class="flex items-center gap-2">
                <UiIcon name="check" class="w-4 h-4 text-se-secondary-dark" />
                <UiText as="span" variant="p-small" class="text-se-secondary-dark font-medium" text="Review submitted" />
            </div>

            <!-- Expired state -->
            <div v-else-if="request.status === 'expired'" class="flex items-center gap-2">
                <UiIcon name="lock" class="w-4 h-4 text-gray-400" />
                <UiText as="span" variant="p-small" class="text-gray-500" text="Request expired" />
            </div>
        </div>

        <!-- Review Aside -->
        <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
            <div class="p-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
                <SubmitReviewForm ref="formRef" :request-id="request.id" :expert="expertData"
                    @success="handleReviewSuccess" @cancel="handleCancel" />
            </div>
        </UiAside>
    </UiCard>
</template>

